import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/feature/LoadingState";
import { useProjects } from "@/hooks/admin/useAdminData";
import type { ProjectListItem, ProjectStatus } from "@/mocks/admin/projects";
import ProjectsToolbar from "./components/ProjectsToolbar";
import ProjectsTable from "./components/ProjectsTable";
import NewProjectModal from "./components/NewProjectModal";
import ProjectsCalendarModal from "./components/ProjectsCalendarModal";

const tabsDef = [
  { key: "all", label: "All Projects" },
  { key: "in-progress", label: "In Progress" },
  { key: "scheduled", label: "Scheduled" },
  { key: "completed", label: "Completed" },
  { key: "on-hold", label: "On Hold" },
  { key: "cancelled", label: "Cancelled" },
];

export default function ProjectsPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [toast, setToast] = useState<string>("");
  const { data: projects, loading, refetch } = useProjects();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: projects.length };
    projects.forEach((p) => {
      c[p.status] = (c[p.status] || 0) + 1;
    });
    return c;
  }, [projects]);

  const tabs = tabsDef.map((t) => ({ ...t, count: counts[t.key] || 0 }));

  const filtered = useMemo(() => {
    let list = projects;
    if (activeTab !== "all") {
      list = list.filter((p) => p.status === (activeTab as ProjectStatus));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.id.toLowerCase().includes(q) ||
          p.customer.toLowerCase().includes(q) ||
          p.fromCity.toLowerCase().includes(q) ||
          p.toCity.toLowerCase().includes(q) ||
          p.moveType.toLowerCase().includes(q),
      );
    }
    return list;
  }, [activeTab, search, projects]);

  const handleCreated = async (project: ProjectListItem) => {
    await refetch();
    setActiveTab(project.status);
    setToast(`Project ${project.id} created for ${project.customer}`);
    setTimeout(() => setToast(""), 3500);
  };

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-ink-500 mb-2">
              <span>Operations</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-ink-900 font-semibold">Projects</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 leading-tight">
              Moving Projects
            </h1>
            <p className="text-sm text-ink-500 mt-1.5 max-w-xl">
              Every move Haulmark has on the books — from the customer's first booking to the final
              signature on the bill of lading.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCalendarOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line"></i>
              Calendar View
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              New Project
            </button>
          </div>
        </div>

        <ProjectsToolbar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          search={search}
          onSearchChange={setSearch}
          exportRows={filtered}
        />

        {loading ? (
          <LoadingState message="Loading projects…" />
        ) : (
          <ProjectsTable projects={filtered} />
        )}
      </div>

      <NewProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreated}
      />

      <ProjectsCalendarModal
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        projects={projects}
      />

      {/* Toast */}
      <div
        className={`fixed bottom-6 right-6 z-[60] transition-all duration-300 ${
          toast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md border border-ink-800 flex items-center gap-2 max-w-sm">
          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
            <i className="ri-check-line text-sm"></i>
          </span>
          {toast}
        </div>
      </div>
    </AdminLayout>
  );
}